import React, { useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

//ui components
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import AddFab from '../AddFab';

//utils
import { getFullDateTime } from '../../../utils/dateTimeUtil';
import MessagesContext from '../../../contexts/messagesContext';
//icons
import Photos from '../../../assets/images/photos.svg';
import useCheckMobileScreen from '../../../hooks/useWidth';
import useCheckAccess from '../../../hooks/useCheckAccess';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        fontSize: 15,
        position: 'relative',
        width: '100%',
        minHeight: 170,
        background: '#fff',
        height: '100%'
    },
    list: {
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: '125px',
        // height: '100%',
        gridGap: '13px',
        flex: 1,
        borderRadius: 9,
        backgroundColor: '#f4f4f4',
        padding: '12px',
        alignItems: 'center',
        overflow: 'auto',
        [theme.breakpoints.down(1025)]: {
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gridAutoFlow: 'unset',
            gridAutoColumns: 'unset',
            borderRadius: 0,
            backgroundColor: '#fff',
            padding: '24px 20px',
            overflow: 'unset'
        },
        [theme.breakpoints.down(768)]: {
            gridTemplateColumns: '1fr 1fr 1fr'
        }
    },
    listItem: ({ noAccess }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        cursor: noAccess ? 'not-allowed' : 'pointer'
    }),
    itemImage: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        flex: '1',
        background: '#fff',
        marginBottom: 5,
        '& img': {
            width: '40%'
        },
        [theme.breakpoints.down(1025)]: {
            borderRadius: '6px',
            border: 'solid 1px #ced2d6',
            minHeight: '100px',
            '& img': {
                height: '100%'
            }
        }
    },
    emptyComponent: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '0.5rem',
        position: 'relative',
        borderBottom: '1px solid #bbb',
        flex: 1
    },
    emptyMessage: {
        flex: 1,
        fontSize: '1rem',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        letterSpacing: 'normal',
        textAlign: 'center',
        color: '#bbb'
    },
    date: {
        fontSize: '12px'
    }
}));

export default function LabImages() {
    const isMobile = useCheckMobileScreen();
    const { messages } = useContext(MessagesContext);
    const { notEditClinical } = useCheckAccess();
    const laboratoryImages = useSelector((store) => store.laboratory?.laboratoryTestImagesResults);
    const classes = useStyles({ isEmptyList: laboratoryImages?.length, noAccess: notEditClinical });
    const history = useHistory();
    const params = useParams();
    const commonPath = useMemo(
        () => `/${params.clinicId}/patient/${params.id}/laboratory`,
        [params]
    );
    const emptyComponent = (
        <div className={classes.emptyComponent}>
            <Typography className={classes.emptyMessage}>{messages.noLabImageFound}</Typography>
        </div>
    );
    const handleAddItem = () => {
        history.push(`${commonPath}/images_result/new`);
    };

    const handleItemClick = (imageResultId) => () => {
        if (!notEditClinical) {
            history.push(`${commonPath}/images_result/${imageResultId}`);
        }
    };

    const _renderItems = useMemo(
        () =>
            laboratoryImages?.map((i) => (
                <div
                    onClick={handleItemClick(i.id)}
                    className={classes.listItem}
                    key={`image-result-${i.id}`}
                    data-testid="labImages"
                >
                    <div className={classes.itemImage}>
                        <img src={Photos} alt="label" />
                    </div>
                    <span className={classes.date}>{getFullDateTime(i.issued_datetime)}</span>
                </div>
            )),
        // eslint-disable-next-line
        [laboratoryImages]
    );

    return (
        <div className={classes.container}>
            {!laboratoryImages?.length ? (
                emptyComponent
            ) : (
                <div className={classes.list}>{_renderItems}</div>
            )}
            <AddFab
                onClick={handleAddItem}
                bottom={isMobile ? '8%' : '22%'}
                right={isMobile ? 35 : 14}
                data-testid="newImage"
                disabled={notEditClinical}
            />
        </div>
    );
}
